import { ChatMessageAttachmentType } from "@jugl-web/rest-api";
import {
  Avatar,
  InteractiveContainer,
  PopoverMenu,
  PopoverMenuSections,
} from "@jugl-web/ui-components/cross-platform";
import { apiUTCToLocalDateTime, cx } from "@jugl-web/utils";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useNotifications } from "@web-src/modules/notifications/providers/NotificationsProvider/useNotifications";
import format from "date-fns/format";
import isThisYear from "date-fns/isThisYear";
import isToday from "date-fns/isToday";
import isEqual from "lodash/isEqual";
import {
  FC,
  memo,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Highlighter from "react-highlight-words";
import { getAttachmentTypeByMimeType, tokenizeMessageBody } from "../../utils";
import useChatInputState, {
  ChatInputState,
} from "../ChatInput/hooks/useChatInputState";
import { ChatListItemLastMessage } from "../ChatListItemLastMessage";
import { ReactComponent as AlertIcon } from "./assets/alert.svg";
import { ReactComponent as BellIcon } from "./assets/bell.svg";
import { ReactComponent as BullhotnIcon } from "./assets/bullhorn.svg";
import { ReactComponent as DraftIcon } from "./assets/draft.svg";
import { ReactComponent as MentionIcon } from "./assets/mention.svg";
import { ReactComponent as NotificationsMuteAllIcon } from "./assets/notifications_mute_all.svg";
import { ReactComponent as NotificationsMuteAllExceptMentionsIcon } from "./assets/notifications_mute_all_except_mentions.svg";
import unreadIndicatorAnimation from "./assets/unread-indicator.json";

export type ChatListItemProps = {
  onClick: () => void;
  isSelected?: boolean;
  searchQuery?: string;
  timestamp?: string;
  chatId: string;
  title: string;
  /** String for the avatar image URL or a JSX element for the custom avatar component */
  img?: string | ReactElement;
  lastMessageContent: ReactNode;
  unreadCount?: number;
  hasChatAccess?: boolean;
  isUnread?: boolean;
  additionalContent?: React.ReactNode;
  showUnreadIndicator?: boolean;
  isBroadcastChat?: boolean;
  menuSections?: PopoverMenuSections;
  isDraftUnavailable?: boolean;
  isMentioned?: boolean;
  isEntityConversation?: boolean;
  variant?: "default" | "inbox";
};

const useChatInputStateAlone = (
  chatId: string,
  isDraftUnavailable?: boolean
) => {
  const chatInputState = useChatInputState();
  const [draft, setDraft] = useState<ChatInputState | null>(null);
  const chatDraft = useMemo(() => {
    if (!chatId || isDraftUnavailable) {
      return null;
    }
    const draftResult = chatInputState.chatsState?.[chatId];
    if (
      !draftResult ||
      (!draftResult.bodyRaw?.trim() &&
        !draftResult.attachments?.length &&
        !draftResult.conference &&
        !draftResult.voiceRecordingFile)
    ) {
      return null;
    }
    return draftResult;
  }, [chatId, chatInputState.chatsState, isDraftUnavailable]);
  useEffect(() => {
    if (isEqual(chatDraft, draft)) {
      return;
    }
    setDraft(chatDraft);
  }, [chatDraft, draft]);
  return chatDraft;
};

const ChatListItemComponent: FC<ChatListItemProps> = ({
  onClick,
  isSelected,
  searchQuery,
  timestamp,
  chatId,
  title,
  img,
  lastMessageContent,
  unreadCount,
  hasChatAccess,
  additionalContent,
  showUnreadIndicator,
  isBroadcastChat,
  menuSections,
  isUnread,
  isDraftUnavailable,
  isMentioned,
  isEntityConversation,
  variant = "default",
}) => {
  const isInboxVariant = variant === "inbox";
  const { dateLocale } = useLanguage();
  const { me } = useMe();

  const draft = useChatInputStateAlone(chatId, isDraftUnavailable);
  const triggeredByContextMenu = useRef(false);

  const time = useMemo(() => {
    let date = null;
    if (draft?.updatedAt && !unreadCount) {
      date = new Date(draft.updatedAt);
    } else if (timestamp) {
      date = apiUTCToLocalDateTime(timestamp);
    }
    if (!date) {
      return null;
    }

    if (isToday(date)) {
      return format(date, "h:mm a", {
        locale: dateLocale,
      });
    }

    return format(
      date,
      isThisYear(date) ? "dd MMM, h:mm a" : "dd MMM yyyy, h:mm a",
      {
        locale: dateLocale,
      }
    );
  }, [draft?.updatedAt, unreadCount, timestamp, dateLocale]);

  const { getChatNotificationsPreferences } = useNotifications();
  const chatNotificationPreferences = useMemo(
    () => chatId && getChatNotificationsPreferences(chatId),
    [chatId, getChatNotificationsPreferences]
  );
  const $notificationsIcon = useMemo(() => {
    switch (chatNotificationPreferences) {
      case "all":
        return <NotificationsMuteAllIcon />;
      case "all_except_mentions":
        return <NotificationsMuteAllExceptMentionsIcon />;
      case "none":
      default:
        return null;
    }
  }, [chatNotificationPreferences]);

  const wrapperClassName = cx(
    "flex cursor-pointer gap-2 transition-colors hover:bg-gray-50",
    isInboxVariant ? "items-start px-6 py-[18px]" : "items-center px-8 py-4",
    { "bg-primary-50 hover:bg-primary-50": isSelected }
  );

  const $listItemContent = (
    <>
      <div
        className={cx(
          "flex min-w-0 flex-1 gap-3 font-secondary",
          isInboxVariant ? "items-start" : "items-center"
        )}
      >
        <div className="relative">
          {typeof img === "object" ? (
            img
          ) : (
            <Avatar
              imageUrl={img}
              size={isInboxVariant ? "md" : "lg"}
              username={title || ""}
              className={cx(
                "border border-solid",
                isInboxVariant && "h-10 w-10",
                isEntityConversation ? "border-primary-700" : "border-grey-300"
              )}
            />
          )}
          {isEntityConversation && (
            <BellIcon className="absolute -top-0.5 -left-0.5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            {isBroadcastChat && <BullhotnIcon className="shrink-0" />}
            <div className="overflow-hidden font-medium text-ellipsis whitespace-nowrap">
              <Highlighter
                autoEscape
                highlightClassName="text-primary bg-transparent"
                highlightTag="span"
                searchWords={[searchQuery || ""]}
                textToHighlight={
                  title ? `${title}${me?.id === chatId ? ` (You)` : ""}` : ""
                }
              />
            </div>
            {$notificationsIcon && (
              <div className="ml-1 h-4 w-4 pr-2">{$notificationsIcon}</div>
            )}
          </div>
          <div
            className={cx(
              "relative mt-[6px] overflow-hidden text-sm leading-[16px] text-ellipsis whitespace-nowrap",
              hasChatAccess ? "text-[#707577]" : "text-[#C94841]"
            )}
          >
            {draft && !unreadCount ? (
              <ChatListItemLastMessage
                payloadVersion={CHAT_MESSAGE_PAYLOAD_VERSION}
                tokenizedMessage={
                  draft.bodyRaw ? tokenizeMessageBody(draft.bodyRaw) : []
                }
                attachments={[
                  ...(draft.attachments?.map(({ file, caption }) => ({
                    type: getAttachmentTypeByMimeType(file.type),
                    name: file.name,
                    size: file.size,
                    mimetype: file.type,
                    caption,
                    uid: "",
                  })) || []),
                  ...(draft.voiceRecordingFile
                    ? [
                        {
                          type: ChatMessageAttachmentType.voice,
                          name: draft.voiceRecordingFile.name,
                          size: draft.voiceRecordingFile.size,
                          mimetype: draft.voiceRecordingFile.type,
                          uid: "",
                        },
                      ]
                    : []),
                ]}
                conferenceInfo={draft.conference}
                isDraft
              />
            ) : (
              lastMessageContent
            )}
          </div>
          {additionalContent && (
            <div className="mt-1.5">{additionalContent}</div>
          )}
        </div>
      </div>
      {(time || !!unreadCount || !hasChatAccess) && (
        <div
          className={cx(
            "flex flex-col items-end gap-1",
            isInboxVariant && "mt-[3px]"
          )}
        >
          {time && (
            <div
              className={cx(
                "text-[13px] text-ellipsis whitespace-nowrap text-[#707577]",
                {
                  "mb-6":
                    !draft && !unreadCount && !showUnreadIndicator && !!draft,
                }
              )}
            >
              {time}
            </div>
          )}
          {hasChatAccess ? (
            <div className="flex items-center gap-1">
              {!!draft && (
                <div className="flex h-6 w-6 items-center justify-center">
                  <DraftIcon />
                </div>
              )}
              {isMentioned && (
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                  <MentionIcon />
                </div>
              )}
              {isUnread && (
                <div
                  className={cx(
                    "flex h-5 items-center justify-center rounded-full bg-[#09B965] px-[6px] text-xs font-medium text-white",
                    {
                      "w-5": !unreadCount || unreadCount <= 9,
                    }
                  )}
                >
                  {unreadCount && unreadCount > 0 ? unreadCount : null}
                </div>
              )}
              {showUnreadIndicator && (
                <div>
                  <Lottie
                    options={{
                      animationData: unreadIndicatorAnimation,
                    }}
                    height={24}
                    width={24}
                  />
                </div>
              )}
            </div>
          ) : (
            <AlertIcon />
          )}
        </div>
      )}
    </>
  );

  if (!menuSections?.length) {
    return (
      <div className={wrapperClassName} onClick={onClick}>
        {$listItemContent}
      </div>
    );
  }

  return (
    <PopoverMenu
      hasBackdrop
      placement="bottom-end"
      sections={menuSections || []}
      renderTrigger={({ Trigger, triggerRef }) => (
        <Trigger
          as={InteractiveContainer}
          ref={triggerRef}
          className={wrapperClassName}
          onContextMenu={(e) => {
            if (!menuSections || !menuSections[0]?.length) return;
            e.preventDefault();
            triggeredByContextMenu.current = true;
            // Dispatch a synthetic click so HeadlessUI PopoverButton opens the panel
            e.currentTarget.dispatchEvent(
              new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
              })
            );
          }}
          onClick={(e) => {
            if (triggeredByContextMenu.current) {
              triggeredByContextMenu.current = false;
              return;
            }
            // Prevent HeadlessUI PopoverButton from toggling on regular left-clicks
            e.preventDefault();
            onClick();
          }}
        >
          {$listItemContent}
        </Trigger>
      )}
    />
  );
};

export const ChatListItem = memo(ChatListItemComponent);
